import { SetStateAction, useEffect, useReducer, useState, useRef } from 'react'
import reducer, {
  initialState,
  setRelationProjectListAction,
  setIsOpenModalAction,
  setCheckedRadioKey,
  setModalListAction,
  setFilteredPlatformList,
} from './reducer'
import {
  GET_BRAND_LIST,
  GET_CATEGORY_LIST,
  GET_PROJECT_LIST,
  GET_UNIT_LIST,
  ProjectListRes,
  ProjectTypeDTOItem,
} from '../../api/MaterialApi'
import { Props } from '.'
import { isResponseCodeSuccess } from '../../tools/util'
import { Form, message } from 'antd'
import { materialTypeEnum } from '@/global/enum/product'
import { mergePlatformList } from '@/tools/util'
import { useTypedSelector } from '../../redux/store'
import { NOT_SELECT_PLATFORM } from '@/enum'

export function convertProjectTypeDTOList(
  list: ProjectListRes[],
): ProjectTypeDTOItem[] {
  return (
    list?.map((item) => {
      return {
        projectName: item.platformName,
        projectType: item.platformCode,
      }
    }) ?? []
  )
}

export function useSetupHook(props: Props) {
  const { onChange, mode, value, form, productType } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { checkedRadioKey, filteredPlatformList } = state
  const { platformList, connectedProjectData } = useTypedSelector((state) => ({
    platformList: state.goodsReducer?.platformList || [],
    connectedProjectData:
      (state.goodsReducer as any)?.connectedProjectData || [],
  }))
  const relationProjectWatch = Form.useWatch('relationProject', form)
  const productTypeWatch =
    mode === 'single'
      ? Form.useWatch('productType', form)
      : productType
        ? materialTypeEnum.getValueByLabel(
            productType as '电商供应商物料' | '供应商物料',
          )
        : ''

  const needCheckSelectedItemRef = useRef<boolean>(false)
  const prevProductTypeRef = useRef<string | undefined>(productTypeWatch)

  const [pagination, setPaginationAction] = useState({
    total: 100,
    current: 1,
    pagesize: 10,
  })

  const [searchValue, setSearchValue] = useState('')
  const [disable, setDisable] = useState(false)
  async function getProjectList() {
    const res = await GET_PROJECT_LIST()
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setRelationProjectListAction(res.data))
    }
  }

  const handleCheckboxGroupChange = (checkedValue: any) => {
    const res: any = filteredPlatformList
      .filter((item) => {
        return checkedValue.includes(item.code)
      })
      .map((item: any) => {
        return { ...item, platformCode: item.code, platformName: item.name }
      })
    onChange?.(res)
  }

  const handleSingleSelectChange = (selectedValue: string) => {
    const res: any = filteredPlatformList
      .filter((item) => {
        return item.code === selectedValue
      })
      .map((item: any) => {
        return { ...item, platformCode: item.code, platformName: item.name }
      })
    onChange?.(res)
  }

  const openModal = (isOpen: boolean) => {
    if (isOpen) {
      getModalList(
        {
          name: searchValue,
          pageNo: pagination.current,
          pageSize: pagination.pagesize,
        },
        checkedRadioKey,
      )
    } else {
      // 重置
      dispatch(setCheckedRadioKey('类目信息'))
      setPaginationAction({
        total: 100,
        current: 1,
        pagesize: 10,
      })
      dispatch(setModalListAction([]))
    }
    dispatch(setIsOpenModalAction(isOpen))
  }

  const handleCheckedChange = (orderPageState: string) => {
    dispatch(setCheckedRadioKey(orderPageState))
    setSearchValue('')
    setPaginationAction({
      total: 100,
      current: 1,
      pagesize: 10,
    })
  }

  const getModalList = async (
    param: {
      name: string | undefined
      pageSize: number
      pageNo: number
    },
    orderPageState: string,
  ) => {
    const res =
      orderPageState === '品牌信息'
        ? await GET_BRAND_LIST(param)
        : orderPageState === '类目信息'
          ? await GET_CATEGORY_LIST(param)
          : await GET_UNIT_LIST(param)
    if (isResponseCodeSuccess(res.code)) {
      if (res.data.content?.length) {
        setDisable(res.data.content?.length < 10)
        const modalList = res.data.content.map((item: any, index: number) => ({
          ...item,
          key: index,
        }))
        dispatch(setModalListAction(modalList))
      } else {
        setDisable(true)
        dispatch(setModalListAction([]))
      }
    } else {
      message.warning(res.msg)
    }
  }

  const previousPage = () => {
    setPaginationAction((prevPagination) => ({
      total: 100,
      current: prevPagination.current === 1 ? 1 : prevPagination.current - 1,
      pagesize: 10,
    }))
  }

  const nextPage = () => {
    setPaginationAction((prevPagination) => ({
      total: 100,
      current: prevPagination.current + 1,
      pagesize: 10,
    }))
  }

  const handleChange = (e: { target: { value: SetStateAction<string> } }) => {
    setSearchValue(e.target.value)
  }

  const onSearch = () => {
    setPaginationAction({
      total: 100,
      current: 1,
      pagesize: 10,
    })
    getModalList(
      {
        name: searchValue,
        pageNo: 1,
        pageSize: pagination.pagesize,
      },
      checkedRadioKey,
    )
  }

  useEffect(() => {
    if (pagination.current > 0) {
      getModalList(
        {
          name: searchValue,
          pageNo: pagination.current,
          pageSize: pagination.pagesize,
        },
        checkedRadioKey,
      )
    }
  }, [pagination])

  useEffect(() => {
    getProjectList()
  }, [])

  const filterPlatformList = (data: any[]) => {
    return data
      .filter((item) => !NOT_SELECT_PLATFORM.includes(item.code))
      .map((item) => {
        return {
          itemFields: item.itemFields,
          pushRequirements: item.pushRequirements, // 物料推送要求
          name: item.name,
          code: item.code,
        }
      })
  }
  useEffect(() => {
    if (materialTypeEnum.isLabelsMatchValue(['供应商物料'], productTypeWatch)) {
      dispatch(
        setFilteredPlatformList(
          filterPlatformList(
            mergePlatformList(connectedProjectData, platformList),
          ),
        ),
      )
    } else {
      dispatch(setFilteredPlatformList(filterPlatformList(platformList)))
    }
  }, [mode, productTypeWatch, platformList, connectedProjectData, dispatch])

  useEffect(() => {
    const prevProductType = prevProductTypeRef.current
    const currentProductType = productTypeWatch

    if (
      prevProductType !== undefined &&
      prevProductType !== currentProductType
    ) {
      needCheckSelectedItemRef.current = true
    }

    prevProductTypeRef.current = currentProductType
  }, [productTypeWatch])
  useEffect(() => {
    if (
      relationProjectWatch &&
      relationProjectWatch.length > 0 &&
      !filteredPlatformList.some(
        (item) => item.code === relationProjectWatch[0]?.code,
      )
    ) {
      onChange?.([])
    }
    needCheckSelectedItemRef.current = false
  }, [filteredPlatformList])
  return {
    state,
    handleCheckboxGroupChange,
    handleSingleSelectChange,
    openModal,
    handleCheckedChange,
    previousPage,
    nextPage,
    pagination,
    onSearch,
    disable,
    handleChange,
    searchValue,
  }
}
import { SetStateAction, useEffect, useReducer, useState, useRef } from 'react'
import reducer, {
  initialState,
  setRelationProjectListAction,
  setIsOpenModalAction,
  setCheckedRadioKey,
  setModalListAction,
  setFilteredPlatformList,
} from './reducer'
import {
  GET_BRAND_LIST,
  GET_CATEGORY_LIST,
  GET_PROJECT_LIST,
  GET_UNIT_LIST,
  ProjectListRes,
  ProjectTypeDTOItem,
} from '../../api/MaterialApi'
import { Props } from '.'
import { isResponseCodeSuccess } from '../../tools/util'
import { Form, message } from 'antd'
import { materialTypeEnum } from '@/global/enum/product'
import { mergePlatformList } from '@/tools/util'
import { useTypedSelector } from '../../redux/store'
import { NOT_SELECT_PLATFORM } from '@/enum'

export function convertProjectTypeDTOList(
  list: ProjectListRes[],
): ProjectTypeDTOItem[] {
  return (
    list?.map((item) => {
      return {
        projectName: item.platformName,
        projectType: item.platformCode,
      }
    }) ?? []
  )
}

export function useSetupHook(props: Props) {
  const { onChange, mode, value, form, productType } = props
  const [state, dispatch] = useReducer(reducer, initialState)
  const { checkedRadioKey, filteredPlatformList } = state
  const { platformList, connectedProjectData } = useTypedSelector((state) => ({
    platformList: state.goodsReducer?.platformList || [],
    connectedProjectData:
      (state.goodsReducer as any)?.connectedProjectData || [],
  }))
  const relationProjectWatch = Form.useWatch('relationProject', form)
  const productTypeWatch =
    mode === 'single'
      ? Form.useWatch('productType', form)
      : productType
        ? materialTypeEnum.getValueByLabel(
            productType as '电商供应商物料' | '供应商物料',
          )
        : ''

  const needCheckSelectedItemRef = useRef<boolean>(false)
  const prevProductTypeRef = useRef<string | undefined>(productTypeWatch)

  const [pagination, setPaginationAction] = useState({
    total: 100,
    current: 1,
    pagesize: 10,
  })

  const [searchValue, setSearchValue] = useState('')
  const [disable, setDisable] = useState(false)
  async function getProjectList() {
    const res = await GET_PROJECT_LIST()
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setRelationProjectListAction(res.data))
    }
  }

  const handleCheckboxGroupChange = (checkedValue: any) => {
    const res: any = filteredPlatformList
      .filter((item) => {
        return checkedValue.includes(item.code)
      })
      .map((item: any) => {
        return { ...item, platformCode: item.code, platformName: item.name }
      })
    onChange?.(res)
  }

  const handleSingleSelectChange = (selectedValue: string) => {
    const res: any = filteredPlatformList
      .filter((item) => {
        return item.code === selectedValue
      })
      .map((item: any) => {
        return { ...item, platformCode: item.code, platformName: item.name }
      })
    onChange?.(res)
  }

  const openModal = (isOpen: boolean) => {
    if (isOpen) {
      getModalList(
        {
          name: searchValue,
          pageNo: pagination.current,
          pageSize: pagination.pagesize,
        },
        checkedRadioKey,
      )
    } else {
      // 重置
      dispatch(setCheckedRadioKey('类目信息'))
      setPaginationAction({
        total: 100,
        current: 1,
        pagesize: 10,
      })
      dispatch(setModalListAction([]))
    }
    dispatch(setIsOpenModalAction(isOpen))
  }

  const handleCheckedChange = (orderPageState: string) => {
    dispatch(setCheckedRadioKey(orderPageState))
    setSearchValue('')
    setPaginationAction({
      total: 100,
      current: 1,
      pagesize: 10,
    })
  }

  const getModalList = async (
    param: {
      name: string | undefined
      pageSize: number
      pageNo: number
    },
    orderPageState: string,
  ) => {
    const res =
      orderPageState === '品牌信息'
        ? await GET_BRAND_LIST(param)
        : orderPageState === '类目信息'
          ? await GET_CATEGORY_LIST(param)
          : await GET_UNIT_LIST(param)
    if (isResponseCodeSuccess(res.code)) {
      if (res.data.content?.length) {
        setDisable(res.data.content?.length < 10)
        const modalList = res.data.content.map((item: any, index: number) => ({
          ...item,
          key: index,
        }))
        dispatch(setModalListAction(modalList))
      } else {
        setDisable(true)
        dispatch(setModalListAction([]))
      }
    } else {
      message.warning(res.msg)
    }
  }

  const previousPage = () => {
    setPaginationAction((prevPagination) => ({
      total: 100,
      current: prevPagination.current === 1 ? 1 : prevPagination.current - 1,
      pagesize: 10,
    }))
  }

  const nextPage = () => {
    setPaginationAction((prevPagination) => ({
      total: 100,
      current: prevPagination.current + 1,
      pagesize: 10,
    }))
  }

  const handleChange = (e: { target: { value: SetStateAction<string> } }) => {
    setSearchValue(e.target.value)
  }

  const onSearch = () => {
    setPaginationAction({
      total: 100,
      current: 1,
      pagesize: 10,
    })
    getModalList(
      {
        name: searchValue,
        pageNo: 1,
        pageSize: pagination.pagesize,
      },
      checkedRadioKey,
    )
  }

  useEffect(() => {
    if (pagination.current > 0) {
      getModalList(
        {
          name: searchValue,
          pageNo: pagination.current,
          pageSize: pagination.pagesize,
        },
        checkedRadioKey,
      )
    }
  }, [pagination])

  useEffect(() => {
    getProjectList()
  }, [])

  const filterPlatformList = (data: any[]) => {
    return data
      .filter((item) => !NOT_SELECT_PLATFORM.includes(item.code))
      .map((item) => {
        return {
          itemFields: item.itemFields,
          pushRequirements: item.pushRequirements, // 物料推送要求
          name: item.name,
          code: item.code,
        }
      })
  }
  useEffect(() => {
    if (materialTypeEnum.isLabelsMatchValue(['供应商物料'], productTypeWatch)) {
      dispatch(
        setFilteredPlatformList(
          filterPlatformList(
            mergePlatformList(connectedProjectData, platformList),
          ),
        ),
      )
    } else {
      dispatch(setFilteredPlatformList(filterPlatformList(platformList)))
    }
  }, [mode, productTypeWatch, platformList, connectedProjectData, dispatch])

  useEffect(() => {
    const prevProductType = prevProductTypeRef.current
    const currentProductType = productTypeWatch

    if (
      prevProductType !== undefined &&
      prevProductType !== currentProductType
    ) {
      needCheckSelectedItemRef.current = true
    }

    prevProductTypeRef.current = currentProductType
  }, [productTypeWatch])
  useEffect(() => {
    if (
      relationProjectWatch &&
      relationProjectWatch.length > 0 &&
      !filteredPlatformList.some(
        (item) => item.code === relationProjectWatch[0]?.code,
      )
    ) {
      onChange?.([])
    }
    needCheckSelectedItemRef.current = false
  }, [filteredPlatformList])
  return {
    state,
    handleCheckboxGroupChange,
    handleSingleSelectChange,
    openModal,
    handleCheckedChange,
    previousPage,
    nextPage,
    pagination,
    onSearch,
    disable,
    handleChange,
    searchValue,
  }
}
